import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { getTeamPlayers, teamShowsAssists, initStatRows, sumGoals } from "../lib/playerStats";
import { useLanguage } from "../context/LanguageContext";

function TeamStatBlock({ participant, rows, setRows, score, showAssists, t }) {
  const totalGoals = sumGoals(rows);
  const target = Number(score) || 0;
  const mismatch = totalGoals !== target;

  const update = (idx, field, value) => {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setRows(rows.map((r, i) => (i === idx ? { ...r, [field]: v } : r)));
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded p-3" data-testid={`stat-team-${participant.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-heading uppercase text-sm text-white truncate">{participant.name}</span>
        <span className={`text-xs font-data ${mismatch ? "text-red-400" : "text-green-400"}`}>
          {totalGoals}/{target} {t("stats.goalsShort")}
        </span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-white/30">
          <span className="flex-1">{t("stats.player")}</span>
          <span className="w-14 text-center">{t("stats.goals")}</span>
          {showAssists && <span className="w-14 text-center">{t("stats.assists")}</span>}
        </div>
        {rows.map((r, idx) => (
          <div key={r.playerId} className="flex items-center gap-2" data-testid={`stat-row-${r.playerId}`}>
            <span className="flex-1 text-sm text-white/80 truncate font-data">{r.playerName}</span>
            <Input
              type="number"
              min={0}
              data-testid={`goals-input-${r.playerId}`}
              value={r.goals}
              onChange={(e) => update(idx, "goals", e.target.value)}
              className="w-14 h-8 input-dark text-center font-data px-1"
            />
            {showAssists && (
              <Input
                type="number"
                min={0}
                data-testid={`assists-input-${r.playerId}`}
                value={r.assists}
                onChange={(e) => update(idx, "assists", e.target.value)}
                className="w-14 h-8 input-dark text-center font-data px-1"
              />
            )}
          </div>
        ))}
      </div>
      {mismatch && (
        <p className="text-red-400 text-xs mt-2" data-testid={`stat-mismatch-${participant.id}`}>
          {t("stats.goalsMustEqualScore")}
        </p>
      )}
    </div>
  );
}

// Self-managing player-stats editor for a single match.
// Mount with key={match.id} so it re-initialises per match.
// Reports [combinedEntries, isValid] via onChange.
export function MatchPlayerStats({ participantA, participantB, scoreA, scoreB, existing, onChange }) {
  const { t } = useLanguage();
  const [rowsA, setRowsA] = useState(() => initStatRows(participantA, existing));
  const [rowsB, setRowsB] = useState(() => initStatRows(participantB, existing));

  const showAssistsA = teamShowsAssists(participantA);
  const showAssistsB = teamShowsAssists(participantB);

  useEffect(() => {
    const combined = [...rowsA, ...rowsB];
    const valid =
      sumGoals(rowsA) === (Number(scoreA) || 0) && sumGoals(rowsB) === (Number(scoreB) || 0);
    onChange(combined, valid);
  }, [rowsA, rowsB, scoreA, scoreB, onChange]);

  if (!participantA || !participantB) return null;

  return (
    <div className="space-y-3" data-testid="match-player-stats">
      <p className="text-white/40 text-xs uppercase tracking-wide">{t("stats.enterPlayerStats")}</p>
      <TeamStatBlock participant={participantA} rows={rowsA} setRows={setRowsA} score={scoreA} showAssists={showAssistsA} t={t} />
      <TeamStatBlock participant={participantB} rows={rowsB} setRows={setRowsB} score={scoreB} showAssists={showAssistsB} t={t} />
    </div>
  );
}

export default MatchPlayerStats;
